<script setup lang="ts">
import { dayjs } from 'element-plus'
import { getStopToEndMsg, stopToEndSectionList, stopToEndSectionRoundMap, type StopToEndInfoRes } from '../data'
import { QuestionFilled, User } from '@element-plus/icons-vue'
import { computed, nextTick, onUnmounted, ref, watch } from 'vue'
import { socketEmitAsync, socketOff, socketOn } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { useUserStore } from '@/stores/modules/user'
import { useCommonStore } from '@/stores/modules/common'

const emits = defineEmits(['back'])

const commonStore = useCommonStore()
const userStore = useUserStore()

const playerCount = ref(0)
const round = ref(0)
const joinIndex = ref<number>()
const sectionList = ref<number[]>([])
const sectionUserRecord = ref<Record<number, number>>({})
const sectionRecordList = ref<number[][]>([])
async function getStopToEndInfo() {
  const res = await socketEmitAsync<StopToEndInfoRes>({
    type: 'getStopToEndInfo',
  })
  joinIndex.value = res.joinIndex
  sectionList.value = res.sectionList
  sectionUserRecord.value = res.sectionUserRecord
  sectionRecordList.value = res.sectionRecordList
  round.value = stopToEndSectionRoundMap[sectionList.value.length]
  playerCount.value = Object.values(sectionUserRecord.value).reduce((pre, item) => (pre += item), 0)
  msgBoxScrollToEnd()
}
async function joinStopToEnd(index: number) {
  if (typeof joinIndex.value === 'number') return ElMessage.warning('您已参赛')
  if (round.value === 4 || dayjs().isAfter(dayjs().hour(20).minute(55))) return ElMessage.warning('该时间段不可参赛')
  ElMessageBox.confirm(
    `<p class="affix">确定选择【${stopToEndSectionList[index].name}】区域站到最后吗？（花费 <span class="gold">100,000</span>金币）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  ).then(async () => {
    await socketEmitAsync({ type: 'joinStopToEnd', data: { index } })
    joinIndex.value = index
    userStore.gold -= 100000
    await getStopToEndInfo()
    ElMessage.success('参赛成功')
  })
}
getStopToEndInfo()

async function msgBoxScrollToEnd() {
  await nextTick()
  const messageBoxEl = document.getElementById('stopToEndMsgBox')
  if (!messageBoxEl) return
  messageBoxEl.scrollTo({
    top: messageBoxEl.scrollHeight - messageBoxEl.clientHeight,
    behavior: 'smooth',
  })
}

const nextRoundTime = computed(() =>
  dayjs()
    .hour(21)
    .minute(round.value * 2)
    .second(0),
)
socketOn({
  type: 'stopToEndExcute',
  callBack: (res: number[]) => {
    if (round.value === 0) getStopToEndInfo()
    sectionList.value = res
    sectionRecordList.value.push(res)
    round.value++
    if (round.value === 4) commonStore.getMailList()
    msgBoxScrollToEnd()
  },
})
watch(
  () => commonStore.isSocketReconnect,
  async (val) => {
    if (!val) return
    getStopToEndInfo()
  },
)
onUnmounted(() => {
  socketOff('stopToEndExcute')
})
</script>

<template>
  <div class="stop-to-end">
    <h2>
      一站到底
      <div class="help-info">
        <el-popover placement="top" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>一站到底说明</h4>
            <p>
              1. 一站到底每日<span class="info">21:00</span>开赛，您可在每日<span class="info">20:55</span>之前花费<span class="gold">100,000</span>
              金币加入赛事。
            </p>
            <p>2. 您需要选择您心仪的区域站到最后并获得丰厚的奖赏。</p>
            <p>
              3. 开赛及之后的每2分钟会坠落一半的区域，存活的玩家将瓜分奖池金币，瓜分比例如下：第<span class="grow">1</span>轮
              <span class="info">10%</span> ；第<span class="grow">2</span>轮 <span class="info">20%</span>；第<span class="grow">3</span>轮
              <span class="info">25%</span>；第<span class="grow">4</span>轮 <span class="info">45%</span>。
            </p>
            <p>4. 开赛时，您无需在场，奖励将在每轮结算后发放至邮件。</p>
          </div>
        </el-popover>
      </div>
    </h2>
    <el-countdown
      class="countdown"
      format="开赛倒计时：HH时 mm分 ss秒"
      :value="dayjs().hour(21).minute(0).second(0)"
      value-style="font-size: 16px"
      v-show="round === 0"
    />
    <el-countdown class="countdown" format="下一轮：mm分 ss秒" :value="nextRoundTime" value-style="font-size: 16px" v-show="round > 0 && round < 4" />
    <p v-show="round === 4" style="text-align: center">今日赛事已结束</p>
    <p style="margin: 0 0 5px 0; font-size: 12px; display: flex; justify-content: space-between">
      <span>参赛人数：{{ playerCount }}</span>
      <span>
        奖池金币：<span class="gold">{{ formatNumberWithCommas(playerCount * 100000) }}</span>
      </span>
    </p>
    <div class="section-list">
      <div
        class="item"
        v-for="(item, index) of stopToEndSectionList"
        :class="{ personal: joinIndex === index, fall: !sectionList.includes(index) }"
        @click="joinStopToEnd(index)"
      >
        <span class="name" :style="{ color: item.color }">
          {{ item.name }}
        </span>
        <span class="player-count">
          <el-icon><User /></el-icon>
          <span>{{ formatNumberWithCommas(sectionUserRecord?.[index] || 0) }}</span>
        </span>
      </div>
    </div>
    <div class="game-info-wrap border-wrap">
      <h4>赛事信息</h4>
      <div class="game-info" id="stopToEndMsgBox">
        <p class="affix" v-for="item of getStopToEndMsg(sectionRecordList, sectionUserRecord, playerCount, joinIndex)" v-html="item"></p>
      </div>
    </div>

    <el-button type="success" plain @click="emits('back')">返回</el-button>
  </div>
</template>

<style lang="scss" scoped>
.stop-to-end {
  height: 100%;
  display: flex;
  flex-flow: column;
  h2 {
    position: relative;
    .help-info {
      position: absolute;
      top: 0;
      right: 10px;
    }
  }
  .countdown {
    text-align: center;
    margin-bottom: 30px;
  }
  .section-list {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(4, 1fr);
    margin-bottom: 10px;
    .item {
      height: 70px;
      display: flex;
      flex-flow: column nowrap;
      border: 1px var(--fontColor) solid;
      padding: 5px;
      justify-content: center;
      .name {
        font-size: 16px;
        font-weight: bold;
        text-align: center;
        margin-bottom: 5px;
      }
      .player-count {
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        i {
          margin-right: 3px;
        }
      }
      &.fall {
        visibility: hidden;
      }
    }
  }
  .game-info-wrap {
    flex: 1;
    margin-bottom: 10px;
    padding: 0 10px 10px 10px;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    .game-info {
      overflow: auto;
      font-size: 14px;
      p {
        line-height: 1.4;
        margin: 10px 0;
      }
    }
  }
  button {
    width: 100%;
  }
}
</style>
